import { Container, Sprite, type DestroyOptions, type Texture } from "pixi.js";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import { POT_SHINE_TUNING } from "../tuning/featureTuning";
import { ART_SCALE, POT_SHINE_LAYOUT } from "./layout";

/** What the shine asks of its art (`SpineArtboard`: the Rive file's conversion, drawn into a texture of its own). */
export interface ShineArt {
  readonly texture: Texture;
  resize(scale: number): void;
  advance(deltaSeconds: number): boolean;
  render(deltaSeconds: number): void;
  dispose(): void;
}

/**
 * The artist's pot shine (`pot_shine.riv`, played as its Spine conversion): a warm red-gold shine behind the three pots
 * — a glowing core and soft rays per pot that sway, cross-fade and breathe — on a loop of its own, always (the user,
 * 2026-10-11). Placed by the mock-up's own coordinates at the art's scale; the scene puts this layer behind the pots, so
 * its rays rise behind them and behind the jackpot plaques. From the moment it is in, it is drawn on the UI's runner at
 * its own pace and at the tuning's share of the screen's pixels: never sped up with the round, never work a round waits
 * for.
 */
export class PotShine extends Container {
  private readonly ambientRunner: PresentationTaskRunner;
  private readonly log: Log;
  private art: ShineArt | null = null;
  /** Frames drawn so far (the dev hook's read: it plays). */
  private drawn = 0;
  /** Device pixels per design unit where the shine plays (its texture's size follows it). */
  private pixelsPerUnit = 1;

  public constructor(input: { ambientRunner: PresentationTaskRunner; log: Log }) {
    super({ label: "PotShine" });
    this.ambientRunner = input.ambientRunner;
    this.log = input.log;
  }

  /** The artist's shine, loaded: in its place behind the pots, playing from now on. */
  public setArt(art: ShineArt): void {
    if (this.destroyed || this.art) {
      art.dispose();
      return;
    }
    this.art = art;
    art.resize(this.canvasScale());
    const { artboard, topLeft } = POT_SHINE_LAYOUT;
    const sprite = new Sprite({ texture: art.texture, label: "Shine" });
    sprite.setSize(artboard.width * ART_SCALE, artboard.height * ART_SCALE);
    sprite.position.set(topLeft.x, topLeft.y);
    this.addChild(sprite);
    art.render(0);
    this.drawn += 1;
    this.play(art);
    this.log("[PotShine] the shine is in");
  }

  /** Whether the shine is in, and how many frames it has drawn (the dev hook's read). */
  public get state(): { loaded: boolean; drawn: number } {
    return { loaded: this.art !== null, drawn: this.drawn };
  }

  /** Device pixels per design unit where the shine plays: its texture is drawn at the tuning's share of that. */
  public setPixelsPerUnit(pixelsPerUnit: number): void {
    if (!(pixelsPerUnit > 0) || pixelsPerUnit === this.pixelsPerUnit) return;
    this.pixelsPerUnit = pixelsPerUnit;
    this.art?.resize(this.canvasScale());
  }

  public override destroy(options?: DestroyOptions): void {
    this.art?.dispose();
    this.art = null;
    super.destroy(options);
  }

  /** The shine drawn at its own pace for as long as it is shown. */
  private play(art: ShineArt): void {
    let last = 0;
    void this.ambientRunner.tickInScope(
      "",
      (elapsed) => {
        // Gone since the last frame: its art is freed.
        if (this.destroyed || this.art !== art) return;
        if (art.advance(elapsed - last)) this.drawn += 1;
        last = elapsed;
      },
      () => this.destroyed || this.art !== art,
    );
  }

  /** Texture pixels per artboard pixel: the art's scale on the screen, at the tuning's share. */
  private canvasScale(): number {
    return ART_SCALE * this.pixelsPerUnit * POT_SHINE_TUNING.resolution;
  }
}
